import { useRef, useState, type ReactNode } from 'react'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Card, CardFooter, CardHeader } from '@/src/elements/card'
import { Modal } from '@/src/elements/modal/Modal'
import { Typography } from '@/src/elements/text/Typography'
import type { TGetCommunityApiData } from '@/src/features/community/api/getCommunity.types'
import { CommunityThumbnail } from '@/src/features/community/ui/components/CommunityThumbnail/CommunityThumbnail'
import type { TShareDataGetter } from '@/src/features/share/models/share.types'
import { ShareModal, type TShareModalProps } from '@/src/features/share/ui/components/ShareModal'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useOnChange } from '@/src/general/hooks/useOnChange'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TSmallPublicationApi } from '../../../api/types/publicationApi.types'

export type TPublicationToShare = TSmallPublicationApi & {
  community: Pick<TGetCommunityApiData, 'id' | 'slug' | 'name' | 'picture' | 'lifetime'>
}

export type TPublicationShareModalProps = Pick<TShareModalProps, 'trackingSource'> & {
  showModal: boolean
  onCancel: VoidFunction
  getPublicationShareData: TShareDataGetter
  getCommunityShareData: TShareDataGetter
  publication: TPublicationToShare | null
}

export const PublicationShareModal = ({
  onCancel,
  publication,
  getPublicationShareData,
  getCommunityShareData,
  showModal,
  trackingSource,
}: TPublicationShareModalProps): ReactNode => {
  // This is used to preserve the publication state when the modal is closed.
  // Community and Publication modal still need the publication state to be able to share it.
  const [preservedPublication, setPreservedPublication] = useState<TPublicationToShare>()

  const [isShareCommunityLoading, setIsShareCommunityLoading] = useState(false)
  const [isSharePublicationLoading, setIsSharePublicationLoading] = useState(false)
  const sharePublicationModalRef = useRef<{ onShare: () => void }>(null)
  const shareCommunityModalRef = useRef<{ onShare: () => void }>(null)

  useOnChange(publication, () => {
    if (publication) setPreservedPublication(publication)
  })

  const { t } = useTranslate()

  const handleShareCommunity = () => shareCommunityModalRef.current?.onShare()
  const handleSharePublication = () => sharePublicationModalRef.current?.onShare()

  const publicationDescription = (
    <Typography bold colors="primary" contrast="low" size="lg" className="p-3">
      {preservedPublication?.name}
    </Typography>
  )

  const communityDescription = (
    <View className="py-4" key="community-description">
      <CommunityThumbnail
        title={preservedPublication?.community.name}
        titleProps={{ bold: true, size: 'md' }}
        imgSource={getImageUrl(publication?.community.picture)}
        imageTestID={TEST_ID.PUBLICATION.REWARD.SHARE_CARD.COMMUNITY_IMG}
        showImage
        ellipsis={false}
      />
    </View>
  )

  return (
    <>
      <Modal
        showModal={showModal}
        onCancel={onCancel}
        testID={TEST_ID.PUBLICATION.SHARE.MODAL}
        title={t('publication.prayed.share.choose.title')}
      >
        <View className="flex flex-col gap-4">
          <View className="px-5 pb-4">
            <Typography>{t('publication.prayed.share.choose.body')}</Typography>
          </View>

          <Card
            className="w-full max-w-[500px] gap-4 bg-secondary-bg-medium p-6"
            shadow="xs"
            testID={TEST_ID.PUBLICATION.REWARD.SHARE_CARD.COMMUNITY}
          >
            <CardHeader className="p-0">
              <CommunityThumbnail
                title={publication?.community.name}
                titleProps={{ bold: true, size: 'md' }}
                imgSource={getImageUrl(publication?.community.picture)}
                imageTestID={TEST_ID.PUBLICATION.REWARD.SHARE_CARD.COMMUNITY_IMG}
                showImage
                ellipsis={false}
              />
            </CardHeader>

            <CardFooter className="p-0">
              <Button
                variant="solid"
                colors="secondary"
                fullWidth
                startIcon={ICON.SHARE}
                testID={TEST_ID.PUBLICATION.REWARD.SHARE_CARD.COMMUNITY_BUTTON}
                onPress={handleShareCommunity}
                isLoading={isShareCommunityLoading}
              >
                {t('publication.prayed.share.community.button')}
              </Button>
            </CardFooter>
          </Card>

          <Card
            className="w-full max-w-[500px] gap-4 bg-primary-bg-light p-6"
            shadow="xs"
            testID={TEST_ID.PUBLICATION.REWARD.SHARE_CARD.PUBLICATION}
          >
            <CardHeader className="flex flex-row gap-3 p-0">
              <Typography colors="primary" size="lg" bold>
                {publication?.name}
              </Typography>
            </CardHeader>

            <CardFooter className="p-0">
              <Button
                variant="solid"
                colors="primary"
                fullWidth
                startIcon={ICON.SHARE}
                testID={TEST_ID.PUBLICATION.REWARD.SHARE_CARD.PUBLICATION_BUTTON}
                onPress={handleSharePublication}
                isLoading={isSharePublicationLoading}
              >
                {t('publication.prayed.share.publication.button')}
              </Button>
            </CardFooter>
          </Card>
        </View>
      </Modal>
      <ShareModal
        title={t('publication.prayed.share.community.modal.title')}
        getShareData={getCommunityShareData}
        description={communityDescription}
        trackingSource={trackingSource}
        onLoadingChange={(isLoading) => setIsShareCommunityLoading(isLoading)}
        ref={shareCommunityModalRef}
      />

      <ShareModal
        title={t('publication.prayed.share.publication.modal.title')}
        getShareData={getPublicationShareData}
        description={publicationDescription}
        trackingSource={trackingSource}
        onLoadingChange={(isLoading) => setIsSharePublicationLoading(isLoading)}
        ref={sharePublicationModalRef}
      />
    </>
  )
}
